/**
 * 曝光数据聚合缓冲区
 * 功能：将多个 content_expose 事件聚合为一个请求，与旧版逻辑保持一致
 *
 * 聚合规则：
 * 1. 按照 pin_source 分组
 * 2. 相同 pin_source 的曝光数据聚合到一个容器中
 * 3. 容器达到 30 个素材或 3 秒后自动提交
 * 4. 页面切换时自动清空所有容器
 */

import { omit, pick } from "lodash";
import { eventBus, TrackEvent } from "../event/EventBus";
import { reporter } from "../reporter/Reporter";
import { TrackerLogger } from "../utils/logger";

interface ExtKey {
  key: string;
  reporterKey: string;
}

interface ContainerOptions {
  delay?: number; // 定时上报时间（ms），默认 3000
  size?: number; // 容器大小，默认 30
  dataKey?: string; // 放入容器的属性key，默认 "pin_id"
  extKeys?: ExtKey[]; // 需要数组上报的字段
  reporterKeys?: {
    ids: string; // ids 字段名，默认 "pin_ids"
    number: string; // number 字段名，默认 "pin_number"
  };
}

/**
 * 曝光数据容器
 * 功能：收集相同 pin_source 的曝光数据，达到条件时自动提交
 */
class ExposeContainer {
  private delay: number;
  private size: number;
  private data: string[]; // pin_id 数组（保持顺序）
  private dataSet: Set<string>; // pin_id 集合（用于去重检查）
  private extData: Record<string, any>[]; // ext 数据数组（保持顺序，与 data 一一对应）
  private common: Record<string, any>; // 共同字段
  private dataKey: string;
  private extKeys: ExtKey[];
  private reporterKeys: { ids: string; number: string };
  private timer: number | null = null;

  constructor({
    delay = 3000,
    size = 30,
    dataKey = "pin_id",
    extKeys = [],
    reporterKeys = {
      ids: "pin_ids",
      number: "pin_number",
    },
  }: ContainerOptions = {}) {
    this.delay = delay;
    this.size = size;
    this.data = [];
    this.dataSet = new Set();
    this.extData = [];
    this.common = {};
    this.dataKey = dataKey;
    this.extKeys = extKeys;
    this.reporterKeys = reporterKeys;
  }

  /**
   * 开启定时提交
   */
  private time(): void {
    const that = this;
    this.timer = window.setTimeout(() => {
      that.submit();
    }, this.delay);
  }

  /**
   * 提交聚合数据
   */
  submit(): void {
    if (this.data.length === 0) return;

    // 构建聚合数据
    const trackData: Record<string, any> = {
      ...this.common,
      [this.reporterKeys.ids]: [...this.data],
      [this.reporterKeys.number]: this.data.length,
    };

    // 额外批量数组字段上报
    this.extKeys.forEach((ext) => {
      // 从 extData 数组中提取对应字段的值（保持顺序）
      const extData = this.extData
        .map((data) => data[ext.key])
        .filter((value) => value !== undefined && value !== null);
      // 过滤全空的
      if (extData.length > 0) {
        trackData[ext.reporterKey || ext.key] = extData;
      }
    });

    // 构建事件对象
    const event: TrackEvent = {
      event_id: 33009, // content_expose 的事件 ID
      event_name: "content_expose",
      data: trackData,
      timestamp: Date.now(),
      metadata: {
        source: "v2",
        sessionId: "",
        traceId: `trace_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      },
    };

    // 发送到事件总线
    eventBus.emit(event);

    // 上报
    reporter.report(event);

    TrackerLogger.track("expose", "content_expose", trackData);

    // 提交后进行容器数据初始化
    this.data = [];
    this.dataSet.clear();
    this.extData = [];
    this.common = {};
    // 清除定时器，重置
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * 添加数据到容器
   */
  push(data: Record<string, any>): void {
    const key = this.dataKey;
    const pinId = data[key] ? String(data[key]) : null;

    if (!pinId) return;

    // 检查是否已存在（避免重复）
    const preSize = this.data.length;
    if (!this.dataSet.has(pinId)) {
      // 添加 pin_id（保持插入顺序）
      this.data.push(pinId);
      this.dataSet.add(pinId);

      // 提取 ext 相关字段（与 pin_id 一一对应）
      const extData: Record<string, any> = {};
      this.extKeys.forEach((ext) => {
        if (data[ext.key] !== undefined) {
          extData[ext.key] = data[ext.key];
        }
      });
      this.extData.push(extData);
    }

    // 容器容量达到触发提交
    if (this.data.length === this.size) {
      this.submit();
      return;
    }

    // 当容器有数据了开启定时提交
    if (this.data.length === 1 && this.timer === null) {
      // 保存共同字段（排除 dataKey 和 extKeys）
      this.common = {
        ...omit(data, [key, ...this.extKeys.map((ext) => ext.key)]),
      };
      this.time();
    }
  }
}

/**
 * 多容器管理器
 * 功能：按照 pin_source 分组管理多个容器
 */
class ExposeBufferManager {
  private containers: ExposeContainer[] = [];
  private keyMap: Record<string, number> = {};
  private key: string = "pin_source";
  private dataKey: string = "pin_id";
  private extKeys: ExtKey[] = [
    { key: "ext", reporterKey: "ext" },
    { key: "content_type", reporterKey: "content_types" },
  ];
  private reporterKeys = {
    ids: "pin_ids",
    number: "pin_number",
  };

  constructor() {
    // 监听页面切换事件，清空所有容器
    if (typeof window !== "undefined") {
      // 监听路由变化（Next.js）
      if ((window as any).__NEXT_ROUTER__) {
        const router = (window as any).__NEXT_ROUTER__;
        if (router?.events) {
          router.events.on("routeChangeStart", () => {
            this.clear();
          });
        }
      }

      // 监听 popstate 事件（浏览器前进/后退）
      window.addEventListener("popstate", () => {
        this.clear();
      });
    }
  }

  /**
   * 添加曝光数据
   */
  add(data: Record<string, any>): void {
    const key = data[this.key];
    if (!key) {
      TrackerLogger.warn(
        "ExposeBufferManager",
        "曝光数据缺少 pin_source，无法聚合",
        data
      );
      return;
    }

    if (this.keyMap[key] === undefined) {
      // 创建新容器
      this.keyMap[key] = this.containers.length;
      const container = new ExposeContainer({
        dataKey: this.dataKey,
        reporterKeys: this.reporterKeys,
        extKeys: this.extKeys,
      });
      this.containers.push(container);
      container.push(data);
    } else {
      // 添加到现有容器
      const index = this.keyMap[key];
      this.containers[index].push(data);
    }
  }

  /**
   * 清空所有容器（页面切换时调用）
   */
  clear(): void {
    this.containers.forEach((container) => {
      container.submit();
    });
    this.containers = [];
    this.keyMap = {};
  }
}

// 导出单例
export const exposeBuffer = new ExposeBufferManager();
